import FullCalendar from '@fullcalendar/react';
import frLocale from '@fullcalendar/core/locales/fr';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import interactionPlugin from '@fullcalendar/interaction';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import resourceDayGridPlugin from '@fullcalendar/resource-daygrid';
import adaptivePlugin from '@fullcalendar/adaptive';
import {
  forwardRef, useImperativeHandle, useRef,
} from 'react';
import {
  CALENDAR_VIEWS,
  DAY_COLUMN_SLOT_MIN_WIDTH,
  DAYS,
  DEFAULT_LONG_PRESS_DELAY,
  DEFAULT_RESOURCE_AREA_WIDTH,
  DEFAULT_SLOT_MIN_WIDTH,
  STACKED_PERIOD_RESOURCE_AREA_WIDTH,
} from '../../../domain/fullCalendar/useFullCalendar';
import cn from '../../../utils/cn';
import styles from './FullCalendar.module.css';
import {
  DEFAULT_END_TIME,
  DEFAULT_SLOT_DURATION,
  DEFAULT_START_TIME,
} from '../../../domain/fullCalendarHeader/useFullCalendarHeader';
import { formatDayColumnSlotLabelForFullCalendar } from '../../../domain/fullCalendar/fullCalendarUseCase';
import useCalendarAutoResize from '../../../hooks/useCalendarAutoResize';

/**
 * @typedef {object} CalendarProps
 * @property {object[]} events - Calendar events
 * @property {object[]} resources - Calendar resources
 * @property {object[]} resourceAreaColumns - Resource area columns configuration
 * @property {boolean} [editable=true] - Whether events are editable
 * @property {boolean} [selectable=true] - Whether time slots are selectable (drag on the calendar)
 * @property {string} [slotDuration='00:30:00'] - Duration of time slots
 * @property {(arg: import('@fullcalendar/core').EventClickArg) => void} [eventClick] - Event click handler
 * @property {string} [slotMinTime='08:00:00'] - Minimum time displayed on the calendar
 * @property {string} [slotMaxTime='18:00:00'] - Maximum time displayed on the calendar
 * @property {(arg: import('@fullcalendar/core').EventDropArg) => void} [eventDrop] - Event drop handler
 * @property {(arg: import('@fullcalendar/core').EventHoveringArg) => void} [onEventHoverIn] - Event hover in handler
 * @property {(arg: import('@fullcalendar/core').EventHoveringArg) => void} [onEventHoverOut] - Event hover out handler
 * @property {(mountArg: import('@fullcalendar/core').EventMountArg) => void} [eventDidMount]
 * @property {(mountArg: import('@fullcalendar/core').EventMountArg) => void} [eventWillUnmount]
 * @property {(arg: import('@fullcalendar/interaction').EventResizeDoneArg) => void} [onEventResize] - Event resize handler (when the event is made longer or shorter)
 * @property {(arg: import('@fullcalendar/core').DateSelectArg) => void} [onSelect]
 * @property {(arg: any) => JSX.Element} [resourceLabelContent]
 * @property {(arg: import('@fullcalendar/resource').ResourceLabelMountArg) => void} [resourceLabelDidMount]
 * @property {(eventInfo: any) => React.ReactNode} [eventContent]
 * @property {object | false} [headerToolbar=null]
 * @property {boolean} [displayDayHeader = true]
 * @property {string} [className]
 * @property {boolean} [hideHeader=false]
 * @property {boolean} [hideHorizontalScrollBar=false]
 * @property {string} [initialDate]
 * @property {boolean} [stickyHeaderDates=false] - Forwarded to FullCalendar; keep false when a parent wrapper is already sticky
 * @property {string} [initialView]
 * @property {object} [duration] - ex: { month: 1 } or { weeks: 2 } or { days: 3 }
 * @property {number} [stickyTop=null] - The top position of the calendar in pixels
 * @property {boolean} [hourHeaders=false] - Figma Slot hour scale (day/week): HH:mm labels every hour
 * @property {boolean} [dayColumns=false] - One column per day (programming week/month views)
 * @property {number} [resourceAreaWidth] - Width of the resource name column
 * @property {number} [slotMinWidth] - Minimum width of timeline slots
 * @property {string} [blockTitle] - Fixed toolbar title for stacked week/month blocks
 */

/**
 * Calendar component
 * @param {CalendarProps} props
 * @param {React.Ref<any>} ref
 * @returns {React.ReactElement}
 */
export const Calendar = forwardRef((/** @type {CalendarProps} */ {
  events,
  resources,
  resourceAreaColumns,
  editable = true,
  selectable = true,
  slotDuration = DEFAULT_SLOT_DURATION,
  eventClick,
  slotMinTime = DEFAULT_START_TIME,
  slotMaxTime = DEFAULT_END_TIME,
  headerToolbar = {
    start: 'title',
    center: '',
    end: 'today prev,next',
  },
  displayDayHeader = true,
  eventDrop,
  onEventHoverIn,
  onEventHoverOut,
  eventDidMount,
  eventWillUnmount,
  onEventResize,
  onSelect,
  resourceLabelContent,
  resourceLabelDidMount,
  eventContent,
  className,
  hideHeader = false,
  hideHorizontalScrollBar = false,
  initialDate = null,
  stickyHeaderDates = false,
  initialView = 'resourceTimeline',
  duration = null,
  stickyTop = null,
  hourHeaders = false,
  dayColumns = false,
  resourceAreaWidth = dayColumns ? STACKED_PERIOD_RESOURCE_AREA_WIDTH : DEFAULT_RESOURCE_AREA_WIDTH,
  slotMinWidth = dayColumns ? DAY_COLUMN_SLOT_MIN_WIDTH : DEFAULT_SLOT_MIN_WIDTH,
  blockTitle = null,
}, ref) => {
  const calendarRef = useRef(null);
  const wrapperRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  useImperativeHandle(ref, () => calendarRef.current, []);
  useCalendarAutoResize(calendarRef, wrapperRef);

  let slotLabelInterval = null;
  if (hourHeaders) {
    slotLabelInterval = '01:00:00';
  } else if (dayColumns) {
    slotLabelInterval = '24:00:00';
  } else if (slotDuration === '00:05:00') {
    slotLabelInterval = '00:30:00';
  }

  /** @type {import('@fullcalendar/core').FormatterInput | undefined} */
  let slotLabelFormat;
  if (hourHeaders) {
    slotLabelFormat = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    };
  } else if (dayColumns) {
    slotLabelFormat = /** @type {import('@fullcalendar/core').FormatterInput} */ (
      formatDayColumnSlotLabelForFullCalendar
    );
  }

  const titleFormat = blockTitle
    ? /** @type {import('@fullcalendar/core').FormatterInput} */ (() => blockTitle)
    : undefined;

  return (
    <div
      ref={wrapperRef}
      style={stickyTop !== null ? { position: 'sticky', top: `${stickyTop}px`, zIndex: 2 } : {}}
      className={cn([className,
        hideHeader && styles['calendar-no-header'],
        events === null && styles['calendar-no-events'],
        hideHorizontalScrollBar && styles['calendar-no-horizontal-scrollbar'],
        hourHeaders && styles['calendar-hour-headers'],
        dayColumns && styles['calendar-day-columns'],
      ])}
    >
      <FullCalendar
        plugins={[resourceTimelinePlugin, resourceTimeGridPlugin, timeGridPlugin, interactionPlugin, dayGridPlugin, resourceDayGridPlugin, adaptivePlugin]}
        resources={resources}
        events={events}
        resourceAreaColumns={resourceAreaColumns}
        editable={editable}
        selectable={selectable}
        eventDidMount={eventDidMount}
        eventWillUnmount={eventWillUnmount}
        slotDuration={slotDuration}
        slotLabelInterval={slotLabelInterval}
        slotLabelFormat={slotLabelFormat}
        eventColor="var(--primary-700)"
        schedulerLicenseKey="GPL-My-Project-Is-Open-Source"
        select={onSelect}
        eventContent={eventContent}
        eventClick={eventClick}
        eventMouseEnter={onEventHoverIn}
        eventMouseLeave={onEventHoverOut}
        eventResize={onEventResize}
        initialView={initialView}
        duration={duration}
        headerToolbar={headerToolbar}
        titleFormat={titleFormat}
        views={CALENDAR_VIEWS}
        datesAboveResources
        slotMinTime={slotMinTime || DEFAULT_START_TIME}
        slotMaxTime={slotMaxTime || DEFAULT_END_TIME}
        eventDrop={eventDrop}
        resourceLabelContent={resourceLabelContent}
        resourceLabelDidMount={resourceLabelDidMount}
        ref={calendarRef}
        allDaySlot={false}
        nowIndicator
        locale="fr"
        locales={[frLocale]}
        firstDay={DAYS.MONDAY}
        height="auto"
        dayHeaders={displayDayHeader}
        stickyHeaderDates={stickyHeaderDates}
        // Timeline: keep horizontal scroll on data updates and date navigation (default scrollTimeReset scrolls to scrollTime).
        scrollTimeReset={false}
        initialDate={initialDate}
        droppable
        dayMaxEventRows={5}
      // Event display as block is used to have a background color on daygrid events
        eventDisplay="block"
        resourceAreaWidth={resourceAreaWidth}
        expandRows
        slotMinWidth={slotMinWidth}
        longPressDelay={DEFAULT_LONG_PRESS_DELAY}
      />
    </div>
  );
});

export default Calendar;
